// ── Reservation component allocation ────────────────────────────────────────
//
// A reservation line is split between stock that is physically allocated to it
// and stock that is expected from a purchase order. When a reservation is
// edited, that split has to be recomputed for every line - carrying forward
// what was already promised rather than starting from scratch, because the
// allocated portion represents stock genuinely set aside for this reservation.
//
// The awkward part is that free stock is consumed as the lines are walked. Two
// lines for the same SKU must not both claim the same units, so the running
// availability is threaded through the loop rather than read fresh each time.

export interface AllocationLine {
  sku: string;
  /** Per-unit quantity for this line, before multiplying by reservation units. */
  qty: number;
}

export interface PriorAllocation {
  allocated: number;
  expected: number;
}

export interface ComputedAllocation {
  sku: string;
  allocated: number;
  expected: number;
}

/**
 * Recompute allocated/expected for every line of a reservation.
 *
 * @param lines             the reservation's component lines, in display order
 * @param prior             existing allocations keyed by SKU, for lines that
 *                          already existed; absent for newly added lines
 * @param available         free stock keyed by SKU
 * @param reservationUnits  how many units of the build are being reserved
 *
 * Neither `prior` nor `available` is mutated; availability is consumed against
 * an internal copy.
 */
export function recomputeAllocations(
  lines: readonly AllocationLine[],
  prior: Readonly<Record<string, PriorAllocation>>,
  available: Readonly<Record<string, number>>,
  reservationUnits: number
): ComputedAllocation[] {
  const free: Record<string, number> = { ...available };
  const units = reservationUnits || 1;

  return lines.map((line) => {
    const totalNeed = line.qty * units;
    const existing = prior[line.sku];

    // A line that is new to the reservation takes what it can from free stock
    // and expects the rest from a PO.
    if (!existing) {
      const avail = free[line.sku] ?? 0;
      const fromStock = Math.min(totalNeed, avail);
      free[line.sku] = avail - fromStock;
      return { sku: line.sku, allocated: fromStock, expected: totalNeed - fromStock };
    }

    const oldTotal = existing.allocated + existing.expected;
    const diff = totalNeed - oldTotal;

    // Quantity unchanged: keep the existing split exactly as it stands. Do not
    // re-derive it from current stock, or an edit to an unrelated line would
    // silently reshuffle this one.
    if (diff === 0) {
      return { sku: line.sku, allocated: existing.allocated, expected: existing.expected };
    }

    // Quantity up: cover the increase from free stock first, expect the rest.
    if (diff > 0) {
      const avail = free[line.sku] ?? 0;
      const fromStock = Math.min(diff, avail);
      free[line.sku] = avail - fromStock;
      return {
        sku: line.sku,
        allocated: existing.allocated + fromStock,
        expected: existing.expected + (diff - fromStock),
      };
    }

    // Quantity down: release the expected portion before touching allocated.
    // Expected stock is only promised, so giving it up costs nothing, whereas
    // allocated stock is physically set aside and worth holding on to.
    //
    // Note the released units are NOT returned to `free`. A later line for the
    // same SKU will not see them. That matches the behaviour this replaced, and
    // only bites when one reservation lists the same SKU on two lines - rare,
    // and worth fixing deliberately rather than as a side effect of this move.
    const release = Math.abs(diff);
    const fromExpected = Math.min(release, existing.expected);
    const fromAllocated = Math.min(release - fromExpected, existing.allocated);
    return {
      sku: line.sku,
      allocated: existing.allocated - fromAllocated,
      expected: existing.expected - fromExpected,
    };
  });
}
